import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import VendorCard from '@/components/vendors/VendorCard';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { stripHtml } from '@/lib/htmlUtils';
import SeoHead from '@/components/SeoHead';

// Mapping for sortiment types
const SORTIMENT_DISPLAY: Record<string, { name: string; color: 'green' | 'pink' | 'purple' | 'blue' | 'yellow' }> = {
  jidlo: { name: 'Jídlo', color: 'green' },
  sladke: { name: 'Sladké', color: 'pink' },
  alko: { name: 'Alko', color: 'purple' },
  nealko: { name: 'Nealko', color: 'yellow' },
};

// Mapping for activity types
const ACTIVITY_TYPE_DISPLAY: Record<string, { name: string; color: 'green' | 'pink' | 'purple' | 'blue' | 'yellow' }> = {
  obleceni: { name: 'Oblečení', color: 'pink' },
  vyrobky: { name: 'Výrobky', color: 'green' },
  sport: { name: 'Sport', color: 'blue' },
  zabava: { name: 'Zábava', color: 'purple' },
  pro_deti: { name: 'Pro děti', color: 'yellow' },
};

const REGION_DISPLAY: Record<string, string> = {
  praha: 'Praha',
  stredocesky: 'Středočeský',
  jihocesky: 'Jihočeský',
  plzensky: 'Plzeňský',
  karlovarsky: 'Karlovarský',
  ustecky: 'Ústecký',
  liberecky: 'Liberecký',
  kralovehradecky: 'Královéhradecký',
  pardubicky: 'Pardubický',
  vysocina: 'Vysočina',
  jihomoravsky: 'Jihomoravský',
  olomoucky: 'Olomoucký',
  zlinsky: 'Zlínský',
  moravskoslezsky: 'Moravskoslezský',
};

const ServiceDiscovery = () => {
  const { isMobile } = useBreakpoint();

  const { data: vendors } = useQuery({
    queryKey: ['homepage-vendors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data;
    },
  });

  const { data: activities } = useQuery({
    queryKey: ['homepage-activities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data;
    },
  });

  // Merge vendors and activities, sort by updated_at, take top 3
  const latestItems = useMemo(() => {
    const vendorItems = (vendors || []).map(v => ({
      type: 'vendor' as const,
      id: v.id,
      name: v.name,
      slug: v.slug,
      hero_image_url: v.hero_image_url,
      hero_image_width: v.hero_image_width,
      hero_image_height: v.hero_image_height,
      offerings_html: v.offerings_html,
      regions: v.regions,
      available_nationwide: v.available_nationwide,
      updated_at: v.updated_at,
      sortiment_types: v.sortiment_types,
      activity_types: [] as string[],
    }));
    const activityItems = (activities || []).map(a => ({
      type: 'activity' as const,
      id: a.id,
      name: a.name,
      slug: a.slug,
      hero_image_url: a.hero_image_url,
      hero_image_width: a.hero_image_width,
      hero_image_height: a.hero_image_height,
      offerings_html: a.offerings_html,
      regions: a.regions,
      available_nationwide: a.available_nationwide,
      updated_at: a.updated_at,
      sortiment_types: [] as string[],
      activity_types: a.activity_types,
    }));
    return [...vendorItems, ...activityItems]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 3);
  }, [vendors, activities]);

  // Count total items (vendors + activities) for stats
  const { data: totalCount } = useQuery({
    queryKey: ['homepage-total-count'],
    queryFn: async () => {
      const [vendorsResult, activitiesResult] = await Promise.all([
        supabase.from('vendors').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('activities').select('id', { count: 'exact', head: true }).eq('is_active', true),
      ]);

      const vendorCount = vendorsResult.count || 0;
      const activityCount = activitiesResult.count || 0;
      return vendorCount + activityCount;
    },
  });

  const getCategories = (sortimentTypes: string[], activityTypes: string[]) => {
    const fromSortiment = (sortimentTypes || [])
      .map(type => SORTIMENT_DISPLAY[type])
      .filter(Boolean);
    const fromActivity = (activityTypes || [])
      .map(type => ACTIVITY_TYPE_DISPLAY[type])
      .filter(Boolean);
    return [...fromSortiment, ...fromActivity];
  };

  const getLocationString = (regions: string[], nationwide: boolean) => {
    if (nationwide) return 'Celá ČR';
    if (!regions || regions.length === 0) return '';
    return REGION_DISPLAY[regions[0]] || regions[0];
  };
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Největší katalog festivalových dodavatelů"
        description="Najděte ty nejlepší stánkaře, techniku a služby pro váš festival. Kompletní databáze ověřených partnerů na jednom místě."
      />
      <Header ctaText="Přidat záznam" />

      <main>
        {/* Hero Section */}
        <section className="container mx-auto px-6 pt-12">
          <div className="bg-slate-50 rounded-5xl p-8 md:p-20 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-soft-blue/40 rounded-full blur-3xl"></div>
            <div className="relative z-10 max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight text-brand-dark leading-tight">
                Kompletní <span className="text-primary underline decoration-slate-200 underline-offset-8">ekosystém</span> pro váš festival
              </h1>
              <p className="text-lg text-slate-500 mb-12 font-medium max-w-2xl mx-auto">
                Od nejlepších stánkařů po kompletní organizaci a technické zajištění. Všechny služby pro úspěšnou akci na jednom místě.
              </p>
              <div className="flex items-center justify-center gap-2 text-slate-400 pb-20">
                <span className="w-12 h-[1px] bg-slate-200"></span>
                <p className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-400">
                  Veškeré informace zde poskytujeme zdarma
                </p>
                <span className="w-12 h-[1px] bg-slate-200"></span>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="container mx-auto px-6 -mt-10 mb-24 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/katalog" className="group bg-white p-8 rounded-4xl border border-slate-100 shadow-xl shadow-slate-200/40 hover:-translate-y-2 transition-transform duration-300 text-center flex flex-col items-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-[#f2994a]/10 flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-[#f2994a] text-4xl">restaurant</span>
              </div>
              <h3 className="text-xl font-extrabold text-brand-dark mb-3 uppercase tracking-tight">Občerstvení</h3>
              <p className="text-slate-500 text-sm mb-8 leading-relaxed">Prověření prodejci jídla a pití pro vaši akci.</p>
              <span className="mt-auto bg-slate-50 text-brand-dark group-hover:bg-primary px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-wider transition-colors w-full">Do katalogu</span>
            </Link>
            <Link to="/aktivity" className="group bg-white p-8 rounded-4xl border border-slate-100 shadow-xl shadow-slate-200/40 hover:-translate-y-2 transition-transform duration-300 text-center flex flex-col items-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-[#27ae60]/10 flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-[#27ae60] text-4xl">play_circle</span>
              </div>
              <h3 className="text-xl font-extrabold text-brand-dark mb-3 uppercase tracking-tight">Aktivity a řemesla</h3>
              <p className="text-slate-500 text-sm mb-8 leading-relaxed">Workshopy, divadlo, řemeslné dílny a doprovodný program.</p>
              <span className="mt-auto bg-slate-50 text-brand-dark group-hover:bg-primary px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-wider transition-colors w-full">Do katalogu</span>
            </Link>
            <Link to="#" className="group bg-white p-8 rounded-4xl border border-slate-100 shadow-xl shadow-slate-200/40 hover:-translate-y-2 transition-transform duration-300 text-center flex flex-col items-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-[#2f80ed]/10 flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-[#2f80ed] text-4xl">groups</span>
              </div>
              <h3 className="text-xl font-extrabold text-brand-dark mb-3 uppercase tracking-tight">Personál</h3>
              <p className="text-slate-500 text-sm mb-8 leading-relaxed">Brigádníci, security, zdravotníci i moderátoři.</p>
              <span className="mt-auto bg-slate-50 text-brand-dark group-hover:bg-primary px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-wider transition-colors w-full">Zobrazit možnosti</span>
            </Link>
            <Link to="#" className="group bg-white p-8 rounded-4xl border border-slate-100 shadow-xl shadow-slate-200/40 hover:-translate-y-2 transition-transform duration-300 text-center flex flex-col items-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-[#9b51e0]/10 flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-[#9b51e0] text-4xl">settings_suggest</span>
              </div>
              <h3 className="text-xl font-extrabold text-brand-dark mb-3 uppercase tracking-tight">Organizace</h3>
              <p className="text-slate-500 text-sm mb-8 leading-relaxed">Technické zajištění, koordinace a management akcí.</p>
              <span className="mt-auto bg-slate-50 text-brand-dark group-hover:bg-primary px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-wider transition-colors w-full">Více informací</span>
            </Link>
          </div>
        </section>

        {/* Just Registered Section - Masonry */}
        <section className="container mx-auto px-6 mb-24">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-extrabold text-brand-dark tracking-tight">Právě se registrovali</h2>
              <p className="text-slate-400 font-medium">Nejnovější partneři v naší databázi</p>
            </div>
            <Link to="/katalog" className="text-sm font-bold text-brand-dark hover:text-primary flex items-center gap-2">
              Zobrazit vše <span className="material-symbols-outlined text-lg">trending_flat</span>
            </Link>
          </div>
          {/* Masonry Container */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
            {latestItems.length > 0 ? (
              latestItems.map((item) => {
                const categories = getCategories(item.sortiment_types, item.activity_types);
                const location = getLocationString(item.regions, item.available_nationwide);
                const description = stripHtml(item.offerings_html);
                const lastUpdated = format(new Date(item.updated_at), 'd. M. yyyy', { locale: cs });
                const href = item.type === 'vendor' ? `/stanek/${item.slug}` : `/aktivita/${item.slug}`;

                return (
                  <VendorCard
                    key={item.id}
                    image={item.hero_image_url || ''}
                    name={item.name}
                    description={description || 'Žádný popis'}
                    location={location || 'Neuvedeno'}
                    categories={categories}
                    lastUpdated={lastUpdated}
                    href={href}
                    imageWidth={item.hero_image_width}
                    imageHeight={item.hero_image_height}
                  />
                );
              })
            ) : (
              <p className="text-slate-500 col-span-3 text-center py-8">Načítám záznamy...</p>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-6 mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-brand-dark rounded-5xl p-10 md:p-16 text-white relative overflow-hidden">
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-primary/20 rounded-full blur-3xl"></div>
              <h3 className="text-3xl font-extrabold mb-4 relative z-10">Máte stánek nebo službu?</h3>
              <p className="text-slate-300 mb-8 relative z-10">Zaregistrujte se do našeho katalogu a buďte vidět na těch nejlepších akcích.</p>
              <a href="/pridat" className="inline-flex items-center gap-2 bg-primary text-brand-dark px-8 py-4 rounded-2xl font-bold text-xs uppercase tracking-wider hover:scale-105 transition-transform relative z-10">
                <span className="material-symbols-outlined text-base">add_circle</span>
                Vytvořit profil zdarma
              </a>
            </div>
            <div className="bg-soft-purple/30 rounded-5xl p-10 md:p-16 relative overflow-hidden">
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl"></div>
              <h3 className="text-3xl font-extrabold text-brand-dark mb-4 relative z-10">Pořádáte akci?</h3>
              <p className="text-slate-500 mb-8 relative z-10">Využijte naši databázi a najděte ty nejlepší partnery pro váš festival.</p>
              <Link to="/katalog" className="inline-flex items-center gap-2 bg-brand-dark text-white px-8 py-4 rounded-2xl font-bold text-xs uppercase tracking-wider hover:scale-105 transition-transform relative z-10">
                <span className="material-symbols-outlined text-base">search</span>
                Prohlédnout katalog
              </Link>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="bg-brand-dark text-white py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row gap-16 items-start">
                <div className="flex-1">
                  <h2 className="text-4xl font-extrabold mb-8 tracking-tight">O portálu <span className="text-primary">PROFESTIVAL.CZ</span></h2>
                  <div className="space-y-6 text-slate-400 text-lg leading-relaxed">
                    <p>Již více než deset let tvoříme most mezi organizátory a těmi, kteří dělají festival festivalem. Naším cílem je kultivovat českou festivalovou scénu a přinášet kvalitu tam, kde byla dříve improvizace.</p>
                    <p>Pomáháme malým lokálním akcím i těm největším hudebním festivalům najít spolehlivé partnery. Od gastronomie po technické zázemí – hlídáme standardy kvality a spolehlivosti.</p>
                    <div className="pt-6 flex gap-12">
                      <div>
                        <div className="text-4xl font-extrabold text-white mb-1">15+</div>
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Let tradice</div>
                      </div>
                      <div>
                        <div className="text-4xl font-extrabold text-white mb-1">{totalCount || 0}+</div>
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Partnerů</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 bg-white/5 p-8 rounded-4xl border border-white/10">
                  <h4 className="text-xl font-bold mb-6 text-primary">Proč s námi spolupracovat?</h4>
                  <ul className="space-y-6">
                    <li className="flex gap-4">
                      <span className="material-symbols-outlined text-primary">verified_user</span>
                      <div>
                        <h5 className="font-bold text-sm mb-1">Prověřená kvalita</h5>
                        <p className="text-slate-500 text-xs">Každý vendor prochází naším interním schvalovacím procesem.</p>
                      </div>
                    </li>
                    <li className="flex gap-4">
                      <span className="material-symbols-outlined text-primary">analytics</span>
                      <div>
                        <h5 className="font-bold text-sm mb-1">Reálné reference</h5>
                        <p className="text-slate-500 text-xs">Transparentní hodnocení od skutečných organizátorů.</p>
                      </div>
                    </li>
                    <li className="flex gap-4">
                      <span className="material-symbols-outlined text-primary">flash_on</span>
                      <div>
                        <h5 className="font-bold text-sm mb-1">Rychlá komunikace</h5>
                        <p className="text-slate-500 text-xs">Poptávky řešíme v reálném čase přímo v naší platformě.</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="container mx-auto px-6 pt-24 mb-24">
          <div className="bg-slate-50 rounded-5xl p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -top-24 -left-24 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
            <h2 className="text-3xl font-extrabold text-brand-dark mb-4 text-center">Co o nás říkají</h2>
            <p className="text-slate-400 text-center mb-12">Zkušenosti z praxe od organizátorů i stánkařů</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-primary p-8 rounded-4xl relative">
                <span className="material-symbols-outlined text-brand-dark/10 text-6xl absolute top-6 right-8">format_quote</span>
                <p className="text-brand-dark mb-8 relative z-10 font-medium italic">"Profestival nám ušetřil desítky hodin práce. Během několika dní jsme našli všechny stánkaře na náš letní festival."</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-dark/10"></div>
                  <div>
                    <h5 className="font-extrabold text-brand-dark text-sm">Martin Novák</h5>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-4xl relative shadow-sm">
                <span className="material-symbols-outlined text-brand-dark/10 text-6xl absolute top-6 right-8">format_quote</span>
                <p className="text-brand-dark mb-8 relative z-10 font-medium italic">"Jako stánkař jsem díky portálu získal přístup k akcím, o kterých jsem dříve jen snil. Profesionální přístup a skvělá podpora."</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-dark/10"></div>
                  <div>
                    <h5 className="font-extrabold text-brand-dark text-sm">Jana Svobodová</h5>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-4xl relative border border-slate-100">
                <span className="material-symbols-outlined text-primary/30 text-6xl absolute top-6 right-8">format_quote</span>
                <p className="text-slate-600 mb-8 relative z-10 font-medium italic">"Hledali jsme personál na poslední chvíli. Profestival nám pomohl zajistit 30 brigádníků během 48 hodin. Neuvěřitelné."</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-200"></div>
                  <div>
                    <h5 className="font-extrabold text-brand-dark text-sm">Petr Černý</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer variant="service" />
    </div>
  );
};

export default ServiceDiscovery;
